<template>
  <v-card class="overflow-auto" elevation="0" style="max-height: 90vh">
    <!-- Header Section -->
    <v-card-title class="px-8 py-6 border-bottom">
      <div class="d-flex align-center justify-space-between w-100">
        <div class="d-flex align-center">
          <v-icon left color="primary" size="28">
            {{ editMode ? 'mdi-pencil' : 'mdi-plus-circle' }}
          </v-icon>
          <div>
            <h2 class="text-h5 font-weight-bold mb-1">
              {{ !editMode ? $t('Create New') : $t('Edit') }}
              {{ $t('Message Automation') }}
            </h2>
            <div v-if="editMode" class="text-body-2 text--secondary">
              Rule ID: #{{ messageRule.id }}
            </div>
          </div>
        </div>
        <div v-if="editMode" class="text-right">
          <div class="text-caption text--secondary">Last edited</div>
          <div class="text-body-2 font-weight-medium">
            {{ parseDate(messageRule.updated_at) }}
          </div>
        </div>
      </div>
    </v-card-title>

    <v-form ref="form" @submit.prevent="save">
      <v-card-text class="px-8 py-6">
        <!-- Basic Information Card -->
        <v-card class="mb-6" outlined>
          <v-card-title class="py-4 bg-primary-lighten-5">
            <v-icon left color="primary">mdi-information</v-icon>
            <span>Basic Information</span>
          </v-card-title>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="messageRule.name"
                  dense
                  outlined
                  :rules="[required]"
                  :label="$t('Rule Name')"
                  prepend-inner-icon="mdi-tag"
                  class="mb-4"
                />
              </v-col>
            </v-row>

            <div class="mb-4">
              <h4 class="text-subtitle-1 font-weight-bold mb-3">
                {{ $t('Send To') }}
              </h4>
              <v-radio-group
                v-model="recipientType"
                row
                class="mt-0"
                @change="onRecipientTypeChange"
              >
                <v-radio :label="$t('Guest')" value="guest" color="primary" />
                <v-radio :label="$t('User')" value="user" color="primary" />
              </v-radio-group>
            </div>

            <v-row v-if="recipientType === 'user'">
              <v-col cols="12" md="6">
                <contractor-select
                  v-model="messageRule.settings.contractors"
                  :disabled="!!messageRule.settings.personnel"
                  :filtered-items="allUsers"
                  label="Select Users"
                  :allow-multiple="true"
                  :initials="true"
                  class="mb-4"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="messageRule.settings.personnel"
                  :disabled="messageRule.settings.contractors?.length > 0"
                  dense
                  clearable
                  persistent-hint
                  :label="$t('Choose Personnel')"
                  outlined
                  :items="personnelRoles"
                  prepend-inner-icon="mdi-account-group"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Triggers & Timing Card -->
        <v-card class="mb-6" outlined>
          <v-card-title class="py-4 bg-success-lighten-5">
            <v-icon left color="success">mdi-clock</v-icon>
            <span>Triggers & Timing</span>
          </v-card-title>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12">
                <h4 class="text-subtitle-1 font-weight-bold mb-3">
                  {{ $t('Status') }}
                </h4>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="messageRule.settings.reservation_status"
                  :items="RESERVATION_STATUSES"
                  :placeholder="$t('Reservation Status')"
                  outlined
                  hide-details
                  dense
                  prepend-inner-icon="mdi-calendar-check"
                />
              </v-col>
              <v-col v-if="isInquiry" cols="12" md="4">
                <v-select
                  v-model="messageRule.settings.sales_status"
                  clearable
                  :items="CONVERSATION_SALES_STATUSES"
                  :placeholder="$t('Sales Status')"
                  outlined
                  hide-details
                  dense
                  prepend-inner-icon="mdi-chart-line"
                  @change="onSalesStatusChange"
                />
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <h4 class="text-subtitle-1 font-weight-bold mb-4">
              {{ $t('Time Settings') }}
            </h4>
            <v-row>
              <v-col
                v-if="
                  messageRule.sent_at_timing !== 'At' &&
                  !isInquiry &&
                  !isReserved &&
                  !isCanceled
                "
                cols="6"
                md="3"
              >
                <v-select
                  v-model="messageRule.sent_at_number"
                  outlined
                  dense
                  hide-details
                  type="number"
                  :items="timeArray"
                  label="Number"
                />
              </v-col>
              <v-col
                v-if="
                  messageRule.sent_at_timing !== 'At' &&
                  !isInquiry &&
                  !isReserved &&
                  !isCanceled
                "
                cols="6"
                md="3"
              >
                <v-select
                  v-model="messageRule.sent_at_period"
                  :items="['Minutes', 'Hours', 'Days', 'Weeks']"
                  :placeholder="$t('Period')"
                  outlined
                  hide-details
                  dense
                />
              </v-col>
              <v-col cols="6" md="3">
                <v-select
                  v-model="messageRule.sent_at_timing"
                  :items="['After', 'Before', 'At', 'Less than']"
                  :placeholder="$t('Timing')"
                  :disabled="isInquiry || isReserved || isCanceled"
                  outlined
                  hide-details
                  dense
                />
              </v-col>
              <v-col cols="6" md="3">
                <div class="d-flex align-center">
                  <span
                    v-if="messageRule.sent_at_timing === 'Less than'"
                    class="text-body-2 mr-2"
                  >
                    till
                  </span>
                  <v-select
                    v-model="messageRule.sent_at_event"
                    :items="sentAtEvents"
                    :placeholder="$t('Event')"
                    :disabled="isInquiry || isReserved || isCanceled"
                    outlined
                    dense
                    hide-details
                    class="flex-grow-1"
                  />
                </div>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <v-card flat color="blue-grey-lighten-5" class="pa-4">
                  <v-checkbox
                    v-model="messageRule.settings.timing.same_day_booking"
                    class="mt-0"
                    hide-details
                    color="primary"
                    :label="
                      $t('Send on confirmation for all same day bookings')
                    "
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Filters & Conditions Card -->
        <v-card class="mb-6" outlined>
          <v-card-title class="py-4 bg-warning-lighten-5">
            <v-icon left color="warning">mdi-filter</v-icon>
            <span>Filters & Conditions</span>
          </v-card-title>
          <v-card-text class="pt-6">
            <!-- Listings Filters -->
            <h5
              class="text-body-1 font-weight-medium mb-3 d-flex align-center text--secondary"
            >
              <v-icon left small color="grey darken-1">mdi-home-group</v-icon>
              {{ $t('Listings Filters') }}
            </h5>
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <multi-property-picker
                  v-model="messageRule.whitelist"
                  :label="$t('Include Listings')"
                  multiple
                  :hide-btns="true"
                />
              </v-col>
              <v-col cols="12" md="6">
                <multi-property-picker
                  v-model="messageRule.blacklist"
                  :disabled="messageRule.whitelist.length !== 0"
                  :label="$t('Exclude Listings')"
                  :hide-btns="true"
                />
              </v-col>
            </v-row>

            <!-- Channels Filters -->
            <h5
              class="text-body-1 font-weight-medium mb-3 d-flex align-center text--secondary"
            >
              <v-icon left small color="grey darken-1"
                >mdi-swap-horizontal</v-icon
              >
              {{ $t('Channels Filters') }}
            </h5>
            <v-row class="mb-4">
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="messageRule.allowed_channels"
                  :disabled="messageRule.ignore_channels.length !== 0"
                  :items="sourceChannels"
                  label="Include Channels"
                  small-chips
                  outlined
                  dense
                  multiple
                  hide-details
                  prepend-inner-icon="mdi-check-circle"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="messageRule.ignore_channels"
                  :disabled="messageRule.allowed_channels.length !== 0"
                  :items="sourceChannels"
                  label="Exclude Channels"
                  small-chips
                  outlined
                  dense
                  multiple
                  hide-details
                  prepend-inner-icon="mdi-cancel"
                />
              </v-col>
            </v-row>
            <v-checkbox
              v-if="
                messageRule.allowed_channels?.includes('Owner') ||
                messageRule.ignore_channels?.includes('Owner')
              "
              v-model="messageRule.settings.include_owner_guests"
              label="Send message to Owner Guests"
              hint="Send message to reservations where owner is not the guest"
              persistent-hint
              dense
              class="mt-2 mb-4"
              color="primary"
            />

            <!-- Send Conditions -->
            <h5
              class="text-body-1 font-weight-medium d-flex align-center text--secondary"
            >
              <v-icon left small color="grey darken-1">mdi-check-circle</v-icon>
              Send only if:
            </h5>
            <v-card flat color="green-lighten-5" class="pa-2 mb-1">
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="messageRule.settings.deposit"
                    :disabled="messageRule.settings.deposit_missing"
                    hide-details
                    color="success"
                    :label="
                      $t('Security deposit has been paid (or not applicable)')
                    "
                  />
                  <v-checkbox
                    v-model="messageRule.settings.deposit_missing"
                    :disabled="messageRule.settings.deposit"
                    hide-details
                    color="success"
                    :label="
                      $t('Security deposit is required and has not been paid')
                    "
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="messageRule.settings.is_moc"
                    :disabled="messageRule.settings.deposit"
                    hide-details
                    color="success"
                    :label="
                      $t(
                        'Is Merchant of Record (Should be paid by the guest directly)'
                      )
                    "
                  />
                  <v-checkbox
                    v-model="messageRule.settings.has_review"
                    hide-details
                    color="success"
                    :label="$t('Guest left a review')"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-card-text>
        </v-card>

        <!-- Advanced Rules Card -->
        <v-card class="mb-6" outlined>
          <v-card-title class="py-4 bg-purple-lighten-5">
            <v-icon left color="purple">mdi-cog</v-icon>
            <span>Advanced Rules</span>
          </v-card-title>
          <v-card-text class="pt-6">
            <v-expansion-panels flat>
              <!-- Pre-checkin Rules -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="showPreCheckinRules"
                      class="mt-0 mr-4"
                      hide-details
                      color="primary"
                      @click.stop
                    />
                    <span class="font-weight-medium">Pre-checkin Rules</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="showPreCheckinRules">
                  <v-radio-group
                    v-model="messageRule.settings.precheckin"
                    class="mt-2"
                    hide-details
                  >
                    <v-radio
                      label="Only Filled"
                      value="filled"
                      color="primary"
                    />
                    <v-radio
                      label="Only Un-Filled"
                      value="unfilled"
                      color="primary"
                    />
                  </v-radio-group>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Balance Rules -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="showPaidRules"
                      class="mt-0 mr-4"
                      hide-details
                      color="primary"
                      @click.stop
                    />
                    <span class="font-weight-medium">Balance Paid Rules</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="showPaidRules">
                  <v-radio-group
                    v-model="messageRule.settings.balance"
                    class="mt-2"
                    hide-details
                  >
                    <v-radio
                      label="Balance due is not zero"
                      value="unpaid"
                      color="primary"
                    />
                    <v-radio
                      label="Balance due is zero"
                      value="paid"
                      color="primary"
                    />
                  </v-radio-group>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Survey Score Rules -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="showScoreRules"
                      class="mt-0 mr-4"
                      hide-details
                      color="primary"
                      @click.stop
                    />
                    <span class="font-weight-medium">Survey Score Rules</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="showScoreRules">
                  <v-radio-group
                    v-model="messageRule.settings.score"
                    class="mt-2 mb-4"
                    hide-details
                  >
                    <v-radio
                      label="Not filled"
                      value="unreviewed"
                      color="primary"
                    />
                    <v-radio
                      label="Bad review (1-2 points)"
                      value="bad"
                      color="primary"
                    />
                    <v-radio
                      label="Mid review (3 points)"
                      value="mid"
                      color="primary"
                    />
                    <v-radio
                      label="Good review (4 points)"
                      value="good"
                      color="primary"
                    />
                    <v-radio
                      label="Excellent review (5 points)"
                      value="excellent"
                      color="primary"
                    />
                  </v-radio-group>
                  <v-select
                    v-model="messageRule.settings.score_channels"
                    :items="REVIEW_CHANNELS"
                    :label="$t('Score channels')"
                    outlined
                    dense
                    multiple
                    hide-details
                    small-chips
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- Availability Rules -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="showAvailabilityRules"
                      class="mt-0 mr-4"
                      hide-details
                      color="primary"
                      @click.stop
                    />
                    <span class="font-weight-medium">Availability Rules</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="showAvailabilityRules">
                  <v-radio-group
                    v-model="messageRule.settings.availability"
                    class="mt-2"
                    hide-details
                  >
                    <v-radio
                      label="Early Check-in is available"
                      value="early_check_in"
                      color="primary"
                    />
                    <v-radio
                      label="Late Check-out is available"
                      value="late_check_out"
                      color="primary"
                    />
                    <v-radio
                      label="Day before is available"
                      value="day_before"
                      color="primary"
                    />
                    <v-radio
                      label="Day after is available"
                      value="day_after"
                      color="primary"
                    />
                  </v-radio-group>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- VIP Guest Rules -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="showVipGuestRules"
                      class="mt-0 mr-4"
                      hide-details
                      color="primary"
                      @click.stop
                    />
                    <span class="font-weight-medium">VIP Guest Rules</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="showVipGuestRules">
                  <div class="mt-2">
                    <v-radio-group
                      v-model="messageRule.settings.vip_guest_status"
                      class="mt-0"
                      hide-details
                    >
                      <v-radio
                        label="VIP Guests Only"
                        value="vip_only"
                        color="primary"
                      />
                      <v-radio
                        label="Non-VIP Guests Only"
                        value="non_vip_only"
                        color="primary"
                      />
                    </v-radio-group>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>

              <!-- VIP Reservation Rules -->
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-checkbox
                      v-model="showVipReservationRules"
                      class="mt-0 mr-4"
                      hide-details
                      color="primary"
                      @click.stop
                    />
                    <span class="font-weight-medium">
                      VIP Reservation Rules
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="showVipReservationRules">
                  <div class="mt-2">
                    <!-- Guest Level VIP -->
                    <div class="mb-4">
                      <v-radio-group
                        v-model="messageRule.settings.vip_reservation_status"
                        class="mt-0"
                        hide-details
                      >
                        <v-radio
                          label="VIP Reservations Only"
                          value="vip_only"
                          color="primary"
                        />
                        <v-radio
                          label="Non-VIP Reservations Only"
                          value="non_vip_only"
                          color="primary"
                        />
                      </v-radio-group>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <!-- AI Configuration Card -->
        <v-card v-if="is_ai || messageRule.is_ai" class="mb-6" outlined>
          <v-card-title class="py-4 bg-indigo-lighten-5">
            <v-icon left color="indigo">mdi-robot</v-icon>
            <span>AI Configuration</span>
          </v-card-title>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="messageRule.ai_agent_type"
                  :items="['CS', 'Sales']"
                  :placeholder="$t('Agent Type')"
                  outlined
                  hide-details
                  dense
                  prepend-inner-icon="mdi-account-tie"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="messageRule.ai_action_type"
                  :items="aiActionTypes"
                  item-text="text"
                  item-value="value"
                  :placeholder="$t('Action Type')"
                  outlined
                  hide-details
                  dense
                  prepend-inner-icon="mdi-lightning-bolt"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="messageRule.ai_command"
                  label="AI Command"
                  outlined
                  dense
                  rows="6"
                  prepend-inner-icon="mdi-code-braces"
                  hint="Provide specific instructions for the AI agent"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Message Content Card -->
        <v-card v-if="!is_ai && !messageRule.is_ai" class="mb-6" outlined>
          <v-card-title class="py-4 bg-orange-lighten-5">
            <v-icon left color="orange">mdi-message-text</v-icon>
            <span>Message Content</span>
          </v-card-title>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="send_in_channels"
                  :items="channels"
                  outlined
                  label="Messaging Channels"
                  :messages="sendInChannelMessages"
                  dense
                  multiple
                  chips
                  :rules="[required]"
                  small-chips
                  :item-disabled="isChannelSelectDisabled"
                  prepend-inner-icon="mdi-send"
                  @change="onChannelChange"
                />
              </v-col>
              <v-col v-if="includesWhatsapp" cols="12" md="4">
                <v-select
                  v-model="messageRule.whatsapp_template_id"
                  label="WhatsApp Template"
                  :items="templateItems"
                  outlined
                  :rules="[required]"
                  messages="Required for WhatsApp"
                  dense
                  return-object
                  prepend-inner-icon="mdi-whatsapp"
                  @change="updateBody"
                />
              </v-col>
              <v-col v-if="includesEmail || includesChannel" cols="12" md="4">
                <v-text-field
                  v-model="messageRule.email_subject"
                  outlined
                  dense
                  :rules="[required]"
                  messages="Required for Email & Channel"
                  placeholder="Subject Line"
                  label="Subject"
                  prepend-inner-icon="mdi-format-title"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-card flat color="grey-lighten-4" class="pa-4">
                  <!-- Rich Text Editor for Email Only -->
                  <text-editor
                    v-if="showRichTextEditor"
                    ref="richTextEditor"
                    v-model="messageRule.message_body"
                    init-full
                    :show-toolbar="true"
                    :show-save-button="false"
                    imgs-folder="auto-messages"
                  />

                  <!-- Plain Textarea for SMS/WhatsApp/Mixed -->
                  <v-textarea
                    v-else
                    v-model="messageRule.message_body"
                    label="Message Body"
                    :readonly="includesWhatsapp"
                    outlined
                    dense
                    :error-messages="
                      hasInvalidVariables
                        ? `Invalid variables found: ${invalidVariables.join(
                            ', '
                          )}`
                        : []
                    "
                    placeholder="Type your message..."
                    prepend-inner-icon="mdi-message-text-outline"
                    rows="6"
                    @keyup="onMsgDown"
                    @blur="saveCursorPosition"
                  />

                  <!-- SMS Character Counter -->
                  <div
                    v-if="isSMS"
                    class="d-flex justify-space-between text-caption mt-2"
                  >
                    <div class="warning--text">
                      <span v-show="showError">
                        <v-icon small color="warning">mdi-alert</v-icon>
                        SMS message can't contain more than
                        {{ smsCharLimit }} chars
                      </span>
                      <span v-if="containsEmoji">
                        <v-icon small color="warning">mdi-alert</v-icon>
                        SMS message can't contain Emoji
                      </span>
                    </div>
                    <div class="font-weight-medium">
                      {{ charCounter }} / {{ smsCharLimit }}
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Message Parameters -->
            <v-row v-if="!includesWhatsapp">
              <v-col cols="12" md="6" class="ml-auto">
                <v-select
                  v-model="selectedParameter"
                  dense
                  clearable
                  persistent-hint
                  messages="Parameters longer than 255 characters will be trimmed"
                  :hint="$t('Template parameters')"
                  :label="$t('Insert Parameter')"
                  outlined
                  :items="parameterItems"
                  prepend-inner-icon="mdi-code-braces"
                  @change="updateMessageBody"
                />
              </v-col>
            </v-row>
            <v-row v-if="websites?.length > 0">
              <v-col cols="12" md="6" class="ml-auto">
                <v-select
                  v-model="selectedWebsiteUrl"
                  dense
                  clearable
                  persistent-hint
                  messages="Select website URL to add listing-specific parameters"
                  :hint="$t('Website URL Parameters')"
                  :label="$t('Insert Website URL Parameter')"
                  outlined
                  :items="websiteUrlItems"
                  prepend-inner-icon="mdi-web"
                  @change="openListingPickerModal"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-card-text>

      <!-- Action Buttons -->
      <v-card-actions class="px-8 py-6 bg-grey-lighten-4">
        <v-spacer />
        <v-btn color="grey" text large class="mr-4" @click.native="close">
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :disabled="loading || showError"
          :loading="loading"
          large
          type="submit"
          elevation="2"
        >
          <v-icon left>mdi-content-save</v-icon>
          {{ $t('Save Rule') }}
        </v-btn>
      </v-card-actions>
    </v-form>

    <v-dialog v-model="showListingPickerModal" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-home-group</v-icon>
          Select Listings for {{ selectedWebsiteUrl }}
        </v-card-title>
        <v-card-text class="pt-4">
          <multi-property-picker
            v-model="selectedListingsForWebsite"
            :label="$t('Select Listings')"
            multiple
            :hide-btns="false"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeListingPickerModal"> Cancel </v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedListingsForWebsite.length"
            @click="insertWebsiteUrlParameters"
          >
            Insert Parameters
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from '@/axios/config'
import { omit } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import CommonFunctions from 'components/mixins/common_functions'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import FormRulesMixin from 'components/mixins/form-rules-mixin'
import MultiPropertyPicker from 'components/multi-property-picker.vue'
import ContractorSelect from 'components/contractor-select.vue'
import TextEditor from 'components/common/text-editor.vue'
import {
  CONVERSATION_SALES_STATUSES,
  RESERVATION_STATUSES,
  PERSONNEL_ROLES,
  MESSAGE_TEMPLATE_PARAMETERS,
  REVIEW_CHANNELS,
} from '@/consts'

export default {
  components: {
    MultiPropertyPicker,
    ContractorSelect,
    TextEditor,
  },
  mixins: [CommonFunctions, PermissionsMixin, FormRulesMixin],
  props: [
    'afterSaveFunc',
    'editMode',
    'currentRule',
    'templates',
    'is_ai',
    'websites',
  ],
  computed: {
    ...mapState('users', ['allUsers']),
    ...mapGetters('reservation', ['channelsItems']),

    sourceChannels() {
      return this.channelsItems || []
    },
    sentAtEvents() {
      const events = ['Check-in', 'Check-out']
      if (this.messageRule.sent_at_timing !== 'Less than') {
        events.push('Confirmation', 'Creation')

        if (this.messageRule.sent_at_timing === 'At') {
          events.push('Receipt creation')
          // Issue #26312. Listed here too so a Room ready rule opened on v1 renders its
          // event instead of binding v-select to a value missing from :items — which shows
          // a blank field and silently retargets the rule on the next interaction.
          events.push('Room ready')
        }

        if (this.isCanceled) {
          events.push('Cancelation')
        }
      }
      return events
    },
    isInquiry() {
      return this.messageRule.settings.reservation_status === 'inquiry'
    },
    isReserved() {
      return (
        this.messageRule.settings.reservation_status === 'reserved' ||
        this.messageRule.settings.reservation_status === 'agent-reserved'
      )
    },
    isCanceled() {
      return this.messageRule.settings.reservation_status === 'canceled'
    },
    isSMS() {
      return this.includesSMS && this.send_in_channels.length === 1
    },
    containsEmoji() {
      const emojiRegex = /[\uD800-\uDFFF]./
      return emojiRegex.test(this.messageRule.message_body)
    },
    aiActionTypes() {
      const allTypes = {
        CS: [
          { value: 'followup', text: 'Follow up' },
          { value: 'verify', text: 'Verify info with guest' },
        ],
        Sales: [
          { value: 'resell', text: 'Resell' },
          { value: 'promotion', text: 'Promotion' },
          { value: 'followup', text: 'Follow up' },
        ],
      }

      if (this.messageRule.ai_agent_type == undefined) {
        return []
      } else {
        return allTypes[this.messageRule.ai_agent_type]
      }
    },
    templateItems() {
      return this.templates.map(t => ({
        ...t,
        text: `${t.name} ${t.lang && t.lang !== 'en' ? `(${t.lang})` : ''}`,
        value: t.id,
      }))
    },
    timeArray() {
      if (this.messageRule.sent_at_period == 'Minutes') {
        return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
      } else {
        return [
          1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
          21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37,
          38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54,
          55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71,
          72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88,
          89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100,
        ]
      }
    },
    includesWhatsapp() {
      return this.send_in_channels.includes('send_whatsapp')
    },
    includesEmail() {
      return this.send_in_channels.includes('send_email')
    },
    includesChannel() {
      return this.send_in_channels.includes('send_channel')
    },
    includesSMS() {
      return this.send_in_channels.includes('send_sms')
    },
    channels() {
      const all = [
        {
          text: 'Email',
          value: 'send_email',
        },
        {
          text: 'SMS',
          value: 'send_sms',
          visibleWhen: () => this.editMode && !!this.messageRule.send_sms,
        },
        {
          text: 'Whatsapp',
          value: 'send_whatsapp',
        },
        {
          text: 'Channel',
          value: 'send_channel',
        },
      ]
      return all.filter(c => !c.visibleWhen || c.visibleWhen())
    },
    sendInChannelMessages() {
      const msgs = []
      if (this.includesChannel)
        msgs.push('Channel fallbacks to email if unavailable')
      if (this.includesSMS || this.includesWhatsapp)
        msgs.push('Whatsapp & sms fallbacks to channel')
      return msgs.join(', ')
    },
    bracketValidation() {
      if (!this.messageRule.message_body) {
        return {
          openCount: 0,
          closeCount: 0,
          isValid: true,
        }
      }

      const messageText = this.messageRule.message_body
      const openBrackets = (messageText.match(/{/g) || []).length
      const closeBrackets = (messageText.match(/}/g) || []).length

      return {
        openCount: openBrackets,
        closeCount: closeBrackets,
        isValid: openBrackets === closeBrackets,
      }
    },
    hasUnmatchedBrackets() {
      return !this.bracketValidation.isValid
    },
    invalidVariables() {
      if (!this.messageRule.message_body) return []

      const messageText = this.messageRule.message_body

      if (!this.bracketValidation.isValid) {
        return ['Unmatched variable brackets']
      }

      const variableRegex = /{{([^}]+)}}/g
      const matches = [...messageText.matchAll(variableRegex)]
      const usedVariables = matches.map(match => match[0])

      const validVariables = this.parameterItems.map(item => item.value)

      // Add validation for website_url parameters with listing IDs
      const websiteUrlPattern = /^{{website_url:[^:]+(:\d+)?}}$/

      return usedVariables.filter(variable => {
        // Check if it's a standard parameter
        if (validVariables.includes(variable)) {
          return false
        }

        // Check if it's a website_url parameter with listing ID
        if (websiteUrlPattern.test(variable)) {
          return false
        }

        return true
      })
    },
    hasInvalidVariables() {
      return this.invalidVariables.length > 0
    },
    hasSalesStatus() {
      return !!this.messageRule.settings.sales_status
    },
    parameterItems() {
      const websites = this.websites
      let params = [...MESSAGE_TEMPLATE_PARAMETERS]
      if (websites?.length > 0) {
        const titleIdx = MESSAGE_TEMPLATE_PARAMETERS.findIndex(
          param => param.value === '{{title}}'
        )
        const websiteParams = websites.map(website => ({
          text: `Listing Website URL: ${website}`,
          value: `{{website_url:${website}}}`,
        }))
        // Insert websiteParams after 'Listing Title'
        params = [
          ...params.slice(0, titleIdx + 1),
          ...websiteParams,
          ...params.slice(titleIdx + 1),
        ]
      }
      return params
    },
    websiteUrlItems() {
      const websites = this.websites
      if (!websites?.length) return []

      return websites.map(website => ({
        text: `${website}`,
        value: website,
      }))
    },
    showRichTextEditor() {
      return this.includesEmail && !this.includesSMS && !this.includesWhatsapp
    },
  },
  data() {
    return {
      CONVERSATION_SALES_STATUSES,
      RESERVATION_STATUSES,
      REVIEW_CHANNELS,
      send_in_channels: [],
      personnelRoles: [...PERSONNEL_ROLES, { text: 'Owner', value: 'owner' }],
      messageRule: this.currentRule
        ? {
            ...this.currentRule,
            settings: {
              timing: { ...this.currentRule.settings.timing },
              ...this.currentRule.settings,
            },
          }
        : {
            listings_ids: [],
            whitelist: [],
            blacklist: [],
            sent_at_period: 'Minutes',
            sent_at_timing: 'At',
            sent_at_event: 'Confirmation',
            sent_at_number: 1,
            allowed_channels: [],
            ignore_channels: [],
            settings: {
              reservation_status: 'confirmed',
              contractor_ids: [],
              timing: {
                same_day_booking: false,
                one_day_res_confirmation: false,
              },
            },
          },
      menu: false,
      smsCharLimit: 150,
      showError: false,
      savedCursorPosition: { start: 0, end: 0 },
      charCounter: 0,
      whatsappTemplate: null,
      showPaidRules: false,
      showScoreRules: false,
      showPreCheckinRules: false,
      showVipGuestRules: false,
      showVipReservationRules: false,
      showAvailabilityRules: false,
      selectedParameter: null,
      selectedWebsiteUrl: null,
      showListingPickerModal: false,
      selectedListingsForWebsite: [],
      recipientType:
        this.currentRule?.settings.contractors?.length ||
        this.currentRule?.settings.personnel
          ? 'user'
          : 'guest',
    }
  },
  mounted() {
    this.charCounter = this.messageRule.message_body?.length
    this.showPreCheckinRules =
      this.messageRule.settings.precheckin !== undefined
    this.showPaidRules = this.messageRule.settings.balance !== undefined
    this.showVipGuestRules =
      this.messageRule.settings.vip_guest_status !== undefined
    this.showVipReservationRules =
      this.messageRule.settings.vip_reservation_status !== undefined
    this.showScoreRules = this.messageRule.settings.score !== undefined
    this.showAvailabilityRules =
      this.messageRule.settings.availability !== undefined
    if (this.isSMS && this.charCounter > 150) {
      this.showError = true
    } else {
      this.showError = false
    }

    if (this.messageRule.settings.timing == undefined) {
      this.messageRule.settings.timing = {
        same_day_booking: false,
        one_day_res_confirmation: false,
      }
    }
    this.whatsappTemplate = this.templates?.find(
      t => t.body === this.messageRule.message_body
    )?.sid
    this.send_in_channels = this.channels
      .filter(c => this.messageRule[c.value])
      .map(c => c.value)
  },
  watch: {
    showRichTextEditor(newVal, oldVal) {
      // Strip HTML when switching from rich editor to plain textarea
      if (
        oldVal === true &&
        newVal === false &&
        this.messageRule.message_body
      ) {
        this.messageRule.message_body = this.stripHtml(
          this.messageRule.message_body
        )
      }
    },
    'messageRule.settings.reservation_status'(val) {
      if (['inquiry', 'reserved', 'agent-reserved'].includes(val)) {
        this.messageRule.sent_at_timing = 'At'
        this.messageRule.sent_at_event = 'Creation'
      }
      if (val === 'canceled') {
        this.messageRule.sent_at_timing = 'At'
        this.messageRule.sent_at_event = 'Cancelation'
        this.$forceUpdate()
      }
      if (val !== 'inquiry') this.messageRule.settings.sales_status = null
    },
    showPreCheckinRules(val) {
      if (!val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'precheckin'
        )
      }
      if (val) {
        this.messageRule.settings = omit(this.messageRule.settings, 'balance')
        this.messageRule.settings = omit(this.messageRule.settings, 'score')
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'score_channels'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'availability'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_guest_status'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_reservation_status'
        )
        this.showScoreRules = false
        this.showPaidRules = false
        this.showAvailabilityRules = false
        this.showVipGuestRules = false
        this.showVipReservationRules = false
      }
    },
    showPaidRules(val) {
      if (!val) {
        this.messageRule.settings = omit(this.messageRule.settings, 'balance')
      }
      if (val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'precheckin'
        )
        this.messageRule.settings = omit(this.messageRule.settings, 'score')
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'score_channels'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'availability'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_guest_status'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_reservation_status'
        )
        this.showScoreRules = false
        this.showPreCheckinRules = false
        this.showAvailabilityRules = false
        this.showVipGuestRules = false
        this.showVipReservationRules = false
      }
    },
    showVipGuestRules(val) {
      if (!val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_guest_status'
        )
      }
      if (val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'precheckin'
        )
        this.messageRule.settings = omit(this.messageRule.settings, 'balance')
        this.messageRule.settings = omit(this.messageRule.settings, 'score')
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'score_channels'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'availability'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_reservation_status'
        )
        this.showScoreRules = false
        this.showPreCheckinRules = false
        this.showPaidRules = false
        this.showAvailabilityRules = false
        this.showVipReservationRules = false
      }
    },
    showVipReservationRules(val) {
      if (!val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_reservation_status'
        )
      }
      if (val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'precheckin'
        )
        this.messageRule.settings = omit(this.messageRule.settings, 'balance')
        this.messageRule.settings = omit(this.messageRule.settings, 'score')
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'score_channels'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'availability'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_guest_status'
        )
        this.showScoreRules = false
        this.showPreCheckinRules = false
        this.showPaidRules = false
        this.showAvailabilityRules = false
        this.showVipGuestRules = false
      }
    },
    showScoreRules(val) {
      if (!val) {
        this.messageRule.settings = omit(this.messageRule.settings, 'score')
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'score_channels'
        )
      }
      if (val) {
        if (!this.messageRule.settings.score_channels) {
          this.messageRule.settings.score_channels = ['survey']
        }
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'precheckin'
        )
        this.messageRule.settings = omit(this.messageRule.settings, 'balance')
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'availability'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_guest_status'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_reservation_status'
        )
        this.showPreCheckinRules = false
        this.showPaidRules = false
        this.showAvailabilityRules = false
        this.showVipGuestRules = false
        this.showVipReservationRules = false
      }
    },
    showAvailabilityRules(val) {
      if (!val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'availability'
        )
      }
      if (val) {
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'precheckin'
        )
        this.messageRule.settings = omit(this.messageRule.settings, 'balance')
        this.messageRule.settings = omit(this.messageRule.settings, 'score')
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_guest_status'
        )
        this.messageRule.settings = omit(
          this.messageRule.settings,
          'vip_reservation_status'
        )
        this.showPreCheckinRules = false
        this.showPaidRules = false
        this.showScoreRules = false
        this.showVipGuestRules = false
        this.showVipReservationRules = false
      }
    },
  },
  methods: {
    stripHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html')
      return doc.body.textContent || ''
    },
    onRecipientTypeChange() {
      this.messageRule.settings.contractors = null
      this.messageRule.settings.personnel = null
    },
    isChannelSelectDisabled(item) {
      if (item.value === 'send_channel')
        return (
          (this.includesEmail && !this.includesChannel) ||
          this.recipientType === 'user'
        )
      if (item.value === 'send_email')
        return this.includesChannel && !this.includesEmail
      if (item.value === 'send_sms')
        return this.includesWhatsapp && !this.includesSMS
      if (item.value === 'send_whatsapp')
        return this.includesSMS && !this.includesWhatsapp
      return false
    },
    close() {
      this.$store.commit('hideModal')
    },
    onChannelChange() {
      if (!this.isSMS) this.showError = false
    },
    onMsgDown(event) {
      if (this.isSMS) {
        if (event.target.value.length > this.smsCharLimit) {
          this.showError = true
        } else if (this.containsEmoji) {
          this.showError = true
        } else {
          this.showError = false
          if (
            event.ctrlKey &&
            (event.code === 'Enter' || event.keyCode === 13)
          ) {
            this.sendMessage()
            return
          }
        }
        this.charCounter = this.messageRule.message_body.length
      } else {
        this.showError = false
      }
    },
    updateBody({ body, id }) {
      this.messageRule.message_body = body
      this.messageRule.whatsapp_template_id = id
    },
    saveCursorPosition(e) {
      const textarea = e.target
      this.savedCursorPosition = {
        start: textarea.selectionStart,
        end: textarea.selectionEnd,
      }
    },
    updateMessageBody(item) {
      if (!item) return

      // Handle rich text editor (email only)
      if (this.showRichTextEditor) {
        this.$refs.richTextEditor?.insertTextAtCursor(item)
        return
      }

      // Handle plain textarea (SMS/channel/mixed)
      const textarea = this.$el.querySelector('textarea')
      if (!textarea) return

      const isFocused = document.activeElement === textarea
      const start = isFocused
        ? textarea.selectionStart
        : this.savedCursorPosition.start
      const end = isFocused
        ? textarea.selectionEnd
        : this.savedCursorPosition.end
      const text = this.messageRule.message_body || ''

      this.messageRule.message_body =
        text.substring(0, start) + item + text.substring(end)

      this.$nextTick(() => {
        textarea.focus()
        const newPosition = start + item.length
        textarea.setSelectionRange(newPosition, newPosition)
      })
    },
    onSalesStatusChange() {
      this.messageRule.sent_at_timing = this.hasSalesStatus ? null : 'At'
      this.messageRule.sent_at_event = this.hasSalesStatus ? null : 'Creation'
    },
    openListingPickerModal(websiteUrl) {
      if (!websiteUrl) return

      this.selectedWebsiteUrl = websiteUrl
      this.selectedListingsForWebsite = []
      this.showListingPickerModal = true
    },
    closeListingPickerModal() {
      this.showListingPickerModal = false
      this.selectedWebsiteUrl = null
      this.selectedListingsForWebsite = []
    },
    insertWebsiteUrlParameters() {
      if (!this.selectedWebsiteUrl || !this.selectedListingsForWebsite.length) {
        return
      }

      const textarea = this.$el.querySelector('textarea')
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = this.messageRule.message_body || ''

      const websiteUrlParams = this.selectedListingsForWebsite
        .map(
          listingId => `{{website_url:${this.selectedWebsiteUrl}:${listingId}}}`
        )
        .join(' ')

      this.messageRule.message_body =
        text.substring(0, start) + websiteUrlParams + text.substring(end)

      this.$nextTick(() => {
        textarea.focus()
        const newPosition = start + websiteUrlParams.length
        textarea.setSelectionRange(newPosition, newPosition)
      })

      this.closeListingPickerModal()
    },
    save() {
      if (this.hasUnmatchedBrackets) {
        return this.$store.dispatch('showError', {
          message: 'Message contains unmatched variable brackets',
        })
      }

      if (this.hasInvalidVariables) {
        const variables = this.invalidVariables.join(', ')
        const message = `Invalid variables found: ${variables}`
        return this.$store.dispatch('showError', { message })
      }

      if (this.$refs.form.validate() && !this.showError) {
        this.channels.forEach(c => {
          this.messageRule[c.value] = this.send_in_channels.includes(c.value)
        })
        if (this.is_ai) {
          this.messageRule.is_ai = true
        }
        if (this.messageRule.sent_at_timing === 'At') {
          this.messageRule.sent_at_period = null
          this.messageRule.sent_at_number = null
        }
        if (!this.messageRule.settings.score) {
          delete this.messageRule.settings.score_channels
        }
        this.$store.commit('updateListingLoading', true)
        if (this.editMode) {
          axios
            .patch(
              `/api/auto-message-rules/${this.messageRule.id}`,
              this.messageRule
            )
            .then(() => {
              this.$store.commit('updateListingLoading', false)
              if (this.afterSaveFunc) {
                this.afterSaveFunc()
              }
              this.close()
            })
            .catch(error => {
              console.error(error)
              this.$store.commit('updateListingLoading', false)
            })
        } else {
          axios
            .post('/api/auto-message-rules', this.messageRule)
            .then(() => {
              this.$store.commit('updateListingLoading', false)

              this.close()
              if (this.afterSaveFunc) {
                this.afterSaveFunc()
              }
            })
            .catch(error => {
              console.error(error)
              this.$store.commit('updateListingLoading', false)
            })
        }
      }
    },
  },
}
</script>
